import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { environment } from '../../../environments/environment';
import { HasRoleDirectiveDirective } from '../../common/directives/has-role/has-role-directive.directive';

interface SubscriptionPrice {
  unit_amount?: number;
  currency?: string;
  product?: string | { id?: string };
}

interface SubscriptionItem {
  price?: SubscriptionPrice;
  current_period_start?: number;
  current_period_end?: number;
}

interface FocussedSubscription {
  status?: string;
  current_period_start?: number;
  current_period_end?: number;
  items?: { data?: SubscriptionItem[] };
}

/** Response of `GET /subscriptions/customer/:id/outstanding-invoice`. */
interface OutstandingInvoice {
  hasOutstanding: boolean;
  hostedInvoiceUrl: string | null;
  invoiceId: string | null;
  amountDue: number | null; // minor units (e.g. pence)
  currency: string | null;
  dueDate: number | null; // unix seconds
  status: string | null;
  subscriptionStatus: string | null;
  paymentMethod: { brand: string | null; last4: string | null } | null;
}

/** Response of `POST /subscriptions/customer/:id/reactivate`. */
interface ReactivateResult {
  success: boolean;
  reason: 'reactivated' | 'already_active' | 'requires_payment' | 'no_payment_method' | 'error';
  status: string | null;
  subscriptionId: string | null;
  hostedInvoiceUrl: string | null;
}

/**
 * Subscription past-due / billing-hold interstitial.
 *
 * Rendered at `/subscription-paused` when {@link CurrentUserService.checkFocussedSubscription}
 * resolves the focussed brand's subscription to `past_due` / `canceled` / `pause_collection`.
 * Single job: explain the failed payment, reassure the user their data is safe, and drive the
 * "Update Payment Method" action (Stripe billing portal) to restore access.
 */
@Component({
  selector: 'app-not-active',
  imports: [HasRoleDirectiveDirective],
  templateUrl: './not-active.html',
  styleUrl: './not-active.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotActive {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly router = inject(Router);

  /** Support contact for the billing-help footer link. */
  readonly supportEmail = 'hello@shoutugc.com';

  private readonly shoutUser = toSignal(this.currentUserService.shoutUser$, { initialValue: null });

  /** Personalised headline name, e.g. "Let's get you back up and running, Grace.". */
  readonly firstName = computed<string>(() => this.shoutUser()?.firstName ?? 'there');

  private readonly subscription = computed<FocussedSubscription | undefined>(
    () => this.currentUserService.currentSubscription() as FocussedSubscription | undefined,
  );

  /** Live outstanding-balance detail from Stripe (real amount / due date / card). */
  readonly outstandingInvoice = signal<OutstandingInvoice | null>(null);
  readonly loadingInvoice = signal<boolean>(false);

  /** Set when the user leaves to pay on Stripe — arms the auto-detect-on-return reconciliation. */
  private readonly paymentInitiated = signal<boolean>(false);
  private readonly reconciling = signal<boolean>(false);

  constructor() {
    void this.loadOutstandingInvoice();

    // Auto-detect payment on return: the hosted invoice / portal open in a new tab, so when the
    // user switches back we re-check status (and reactivate a paid-off canceled sub) automatically.
    const onReturn = (): void => {
      if (document.visibilityState === 'visible') {
        void this.reconcileAfterPayment();
      }
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    });
  }

  /** Fetch the real outstanding invoice so the screen shows accurate figures and a pay link. */
  private async loadOutstandingInvoice(): Promise<void> {
    const customerId = this.currentUserService.focussedBrand?.stripeCustomerId;
    if (!customerId) {
      return;
    }
    this.loadingInvoice.set(true);
    try {
      const invoice = (await firstValueFrom(
        this.currentUserService.getOutstandingInvoice(customerId),
      )) as OutstandingInvoice | null;
      this.outstandingInvoice.set(invoice ?? null);
    } catch (error) {
      console.error('Failed to load outstanding invoice', error);
    } finally {
      this.loadingInvoice.set(false);
    }
  }

  /** Live subscription status — prefer the invoice endpoint's value, fall back to the cached sub. */
  private readonly subscriptionStatus = computed<string | undefined>(
    () => this.outstandingInvoice()?.subscriptionStatus ?? this.subscription()?.status,
  );

  /** Plan name for the lede copy — derived from the live subscription's base line item. */
  readonly planName = computed<string>(() => {
    const items = this.subscription()?.items?.data ?? [];
    for (const item of items) {
      const name = this.mapPlanName(this.productId(item.price?.product));
      if (name) {
        return name;
      }
    }
    return 'Studio Pro';
  });

  /**
   * Amount due — the real outstanding invoice total when we have it, otherwise an approximation
   * summed from the subscription line items (fallback while the invoice fetch is in flight / empty).
   */
  readonly amountDue = computed<string>(() => {
    const invoice = this.outstandingInvoice();
    if (invoice && typeof invoice.amountDue === 'number' && invoice.amountDue > 0) {
      const currency = (invoice.currency ?? 'gbp').toUpperCase();
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(
        invoice.amountDue / 100,
      );
    }
    const items = this.subscription()?.items?.data ?? [];
    let total = 0;
    let currency = 'GBP';
    for (const item of items) {
      if (typeof item.price?.unit_amount === 'number') {
        total += item.price.unit_amount / 100;
      }
      if (item.price?.currency) {
        currency = item.price.currency.toUpperCase();
      }
    }
    if (total <= 0) {
      return this.fallbackAmount;
    }
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(total);
  });

  private readonly fallbackAmount = '£249.00';

  /**
   * Date the current unpaid invoice was due. For a `past_due` subscription Stripe creates/charges the
   * renewal invoice at the start of the current period, so `current_period_start` is the "due since"
   * date (a past date). `current_period_end` would be the next renewal (~a month out) — wrong here.
   * Unix seconds → JS Date, mirroring `brand-billing` `convertDate`.
   */
  private readonly dueDate = computed<Date | null>(() => {
    const invoiceDue = this.outstandingInvoice()?.dueDate;
    if (typeof invoiceDue === 'number' && invoiceDue > 0) {
      return new Date(invoiceDue * 1000);
    }
    const sub = this.subscription();
    const periodStart = sub?.items?.data?.[0]?.current_period_start ?? sub?.current_period_start;
    return typeof periodStart === 'number' && periodStart > 0 ? new Date(periodStart * 1000) : null;
  });

  /** "Due since" — the date the unpaid invoice was due, e.g. "16 June 2026". */
  readonly dueSince = computed<string>(() => {
    const due = this.dueDate();
    return due ? this.formatDate(due) : '—';
  });

  /** Grace-period deadline — one calendar month after the invoice due date. */
  readonly gracePeriodEndsAt = computed<string>(() => {
    const due = this.dueDate();
    if (!due) {
      return '—';
    }
    const grace = new Date(due);
    grace.setMonth(grace.getMonth() + 1);
    return this.formatDate(grace);
  });

  /** Whole days the invoice is overdue — derived from the same due date so the strip stays consistent. */
  readonly daysOverdue = computed<number>(() => {
    const due = this.dueDate();
    if (!due) {
      return 0;
    }
    return Math.max(0, Math.floor((Date.now() - due.getTime()) / 86400000));
  });

  /** Real default card on file (brand + last4), or null when none is available to show. */
  readonly paymentMethodLabel = computed<string>(() => {
    const pm = this.outstandingInvoice()?.paymentMethod;
    if (!pm?.last4) {
      return '';
    }
    const brand = pm.brand ? this.titleCase(pm.brand) : 'Card';
    return `${brand} ending ${pm.last4}`;
  });

  // ── Action state ──────────────────────────────────────────────────────────
  readonly openingPortal = signal<boolean>(false);
  readonly paying = signal<boolean>(false);
  readonly reactivating = signal<boolean>(false);
  readonly retrying = signal<boolean>(false);
  readonly loggingOut = signal<boolean>(false);
  readonly actionError = signal<string | null>(null);
  readonly payError = signal<string | null>(null);
  /** Neutral next-step guidance shown while the user is paying in the other tab. */
  readonly payNotice = signal<string | null>(null);

  /** True when there is a real balance the user can pay right now. */
  readonly hasOutstanding = computed<boolean>(() => this.outstandingInvoice()?.hasOutstanding ?? false);

  /** Whether the canceled-subscription re-subscribe path applies (no payable balance). */
  readonly isCanceled = computed<boolean>(() => this.subscriptionStatus() === 'canceled');

  /** Primary CTA label — drives which action fires from the single main button. */
  readonly primaryCtaLabel = computed<string>(() => {
    if (this.hasOutstanding()) {
      return `Pay ${this.amountDue()} now`;
    }
    if (this.isCanceled()) {
      return 'Reactivate subscription';
    }
    return 'Update Payment Method';
  });

  /** Loading state for the primary button (pay / portal / reactivate are all async). */
  readonly primaryBusy = computed<boolean>(
    () => this.paying() || this.openingPortal() || this.reactivating(),
  );

  // ── Staff jump-in (roles 4 / 6) ───────────────────────────────────────────
  readonly showJumpInModal = signal<boolean>(false);
  readonly apiKeyInput = signal<string>('');
  readonly jumpInLoading = signal<boolean>(false);
  readonly jumpInError = signal<string | null>(null);

  openJumpInModal(): void {
    this.showJumpInModal.set(true);
    this.apiKeyInput.set('');
    this.jumpInError.set(null);
  }

  cancelJumpIn(): void {
    this.showJumpInModal.set(false);
    this.apiKeyInput.set('');
    this.jumpInError.set(null);
  }

  async confirmJumpIn(): Promise<void> {
    if (!this.apiKeyInput() || this.jumpInLoading()) {
      return;
    }
    this.jumpInLoading.set(true);
    this.jumpInError.set(null);
    try {
      await this.firebaseService.reenterImpersonation(this.apiKeyInput());
      this.showJumpInModal.set(false);
      this.apiKeyInput.set('');
    } catch {
      this.jumpInError.set('Failed to jump in. Check your API key and try again.');
    } finally {
      this.jumpInLoading.set(false);
    }
  }

  /** Dispatches the single primary button to the right action for the current state. */
  async onPrimaryCta(): Promise<void> {
    if (this.hasOutstanding()) {
      await this.payOutstanding();
      return;
    }
    if (this.isCanceled()) {
      await this.reactivate();
      return;
    }
    await this.updatePaymentMethod();
  }

  /** Pay the outstanding balance on Stripe's hosted invoice page. */
  async payOutstanding(): Promise<void> {
    if (this.paying()) {
      return;
    }
    const url = this.outstandingInvoice()?.hostedInvoiceUrl;
    if (!url) {
      // No payable invoice surfaced — fall back to the billing portal so the user isn't stuck.
      await this.updatePaymentMethod();
      return;
    }
    this.payError.set(null);
    this.payNotice.set(null);
    this.paying.set(true);
    try {
      this.paymentInitiated.set(true);
      window.open(url, '_blank');
      this.payNotice.set('Complete your payment in the new tab — we’ll restore your access automatically.');
    } catch (error) {
      console.error('Failed to open hosted invoice', error);
      this.payError.set('We couldn’t open the payment page. Please try again.');
    } finally {
      this.paying.set(false);
    }
  }

  /**
   * Reactivate a canceled subscription by charging the card on file immediately.
   * A canceled Stripe subscription is terminal, so the API creates a fresh subscription on the
   * same plan (no trial) and charges the saved card. On success we drop the user back into the app;
   * if the charge needs attention we open the hosted invoice page; with no card we open the portal.
   */
  async reactivate(): Promise<void> {
    if (this.reactivating()) {
      return;
    }
    const customerId = this.currentUserService.focussedBrand?.stripeCustomerId;
    if (!customerId) {
      this.payError.set('We couldn’t find your billing account. Please contact billing support.');
      return;
    }
    this.payError.set(null);
    this.payNotice.set(null);
    this.reactivating.set(true);
    try {
      const result = (await firstValueFrom(
        this.currentUserService.reactivateSubscription(customerId),
      )) as ReactivateResult | null;

      if (result?.success) {
        await this.currentUserService.refreshFocussedSubscription();
        this.router.navigate(['/dashboard']);
        return;
      }
      if (result?.reason === 'no_payment_method') {
        await this.updatePaymentMethod();
        return;
      }
      if (result?.reason === 'requires_payment' && result.hostedInvoiceUrl) {
        this.paymentInitiated.set(true);
        window.open(result.hostedInvoiceUrl, '_blank');
        this.payNotice.set(
          'Finish your payment in the new tab — we’ll restore your access automatically.',
        );
        return;
      }
      this.payError.set('We couldn’t reactivate your subscription. Please update your card and try again.');
    } catch (error) {
      console.error('Failed to reactivate subscription', error);
      this.payError.set('We couldn’t reactivate your subscription. Please try again.');
    } finally {
      this.reactivating.set(false);
    }
  }

  /** Secondary CTA — open the Stripe billing portal so the user can update their card. */
  async updatePaymentMethod(): Promise<void> {
    if (this.openingPortal()) {
      return;
    }
    const customerId = this.currentUserService.focussedBrand?.stripeCustomerId;
    if (!customerId) {
      this.actionError.set('We couldn’t find your billing account. Please contact billing support.');
      return;
    }
    this.actionError.set(null);
    this.openingPortal.set(true);
    try {
      const returnUrl = `${window.location.protocol}//${window.location.host}/dashboard`;
      const session = (await firstValueFrom(
        this.currentUserService.createBillingPortalSession(customerId, returnUrl),
      )) as { url?: string };
      if (session?.url) {
        window.open(session.url, '_blank');
      } else {
        this.actionError.set('We couldn’t open the billing portal. Please try again.');
      }
    } catch (error) {
      console.error('Failed to open billing portal', error);
      this.actionError.set('We couldn’t open the billing portal. Please try again.');
    } finally {
      this.openingPortal.set(false);
    }
  }

  /** Lets a stuck user leave the hold screen when they cannot pay or update billing. */
  async logout(): Promise<void> {
    if (this.loggingOut()) {
      return;
    }
    this.loggingOut.set(true);
    try {
      await firstValueFrom(this.firebaseService.logout());
    } catch (error) {
      console.error('Failed to log out', error);
      this.loggingOut.set(false);
    }
  }

  /** Secondary CTA — manual "I've paid" re-check; mirrors the automatic on-return reconciliation. */
  async retryPayment(): Promise<void> {
    if (this.retrying()) {
      return;
    }
    this.actionError.set(null);
    this.retrying.set(true);
    try {
      const restored = await this.restoreAccessAfterPayment();
      if (!restored) {
        this.actionError.set('That payment didn’t go through — try updating your card.');
      }
    } catch (error) {
      console.error('Failed to retry payment', error);
      this.actionError.set('That payment didn’t go through — try updating your card.');
    } finally {
      this.retrying.set(false);
    }
  }

  /**
   * Automatic on-return reconciliation: fires when the user switches back to this tab after being
   * sent to Stripe to pay. Only acts when a payment was actually initiated, and guards against
   * re-entrancy so overlapping focus/visibility events can't double-charge.
   */
  private async reconcileAfterPayment(): Promise<void> {
    if (!this.paymentInitiated() || this.reconciling()) {
      return;
    }
    this.reconciling.set(true);
    try {
      await this.restoreAccessAfterPayment();
      // Disarm only once fully restored. If reactivation re-opened a hosted invoice, stay armed so
      // the next return (after that payment) is detected too.
      if (this.currentUserService.subscriptionActive()) {
        this.paymentInitiated.set(false);
      }
    } catch (error) {
      console.error('Failed to reconcile after payment', error);
    } finally {
      this.reconciling.set(false);
    }
  }

  /**
   * Re-checks status after a payment and restores access:
   *  - subscription now active/trialing → straight to the dashboard;
   *  - balance cleared but the subscription is canceled (terminal) → auto-reactivate (a fresh
   *    subscription + charge) so paying the balance also brings the account back.
   * Returns true when it routed/handed off, false when the payment isn't reflected yet.
   */
  private async restoreAccessAfterPayment(): Promise<boolean> {
    await this.currentUserService.refreshFocussedSubscription();
    if (this.currentUserService.subscriptionActive()) {
      this.router.navigate(['/dashboard']);
      return true;
    }
    await this.loadOutstandingInvoice();
    const stillOutstanding = this.outstandingInvoice()?.hasOutstanding ?? false;
    if (!stillOutstanding && this.isCanceled()) {
      await this.reactivate();
      return true;
    }
    return false;
  }

  /** Capitalises a Stripe card brand for display, e.g. "visa" → "Visa". */
  private titleCase(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /** Formats a date as the design's full-month style, e.g. "16 June 2026". */
  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  private productId(product: SubscriptionPrice['product']): string | undefined {
    if (!product) {
      return undefined;
    }
    return typeof product === 'string' ? product : product.id;
  }

  /** Maps a Stripe base-plan product id to its display name (bolt-ons return undefined). */
  private mapPlanName(productId: string | undefined): string | undefined {
    if (!productId) {
      return undefined;
    }
    const stripe = environment.stripe;
    if (productId === stripe.shaas21products.sub_essential) {
      return 'Essential Plan';
    }
    if (productId === stripe.products.essentials) {
      return 'Essentials Plan';
    }
    if (productId === stripe.products.pro) {
      return 'Pro Plan';
    }
    return undefined;
  }
}
